import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import './assets/style.css'
import Main from './views/Main';
import Login from './views/Login';
import ActivateAccount from './views/account-activation/ActivateAccount';
import SelectOrganization from './views/account-activation/SelectOrganization';
import ForgotPassword from './views/forgot-password/ForgotPassword';
import { WebSocketProvider } from './services/webSocket/webSocketContext';
import ProtectedRoute from './views/ProtectedRoute';
import InstructionsFromDocx from './views/instructions/UpdatedInstructionsPage';
import Support from './views/support/Support';
import BrowserWarningBanner from './components/BrowserWarningBanner';
import UpdateBanner from './components/UpdateBanner';
import PasswordResetModal from './components/PasswordResetModal';
import CookieBanner from './components/CookieBanner';
import { setBrowserInfo } from './slicers/BrowserSlice';
import { detectBrowser } from './utils/browserUtils';
import { checkForUpdates } from './utils/versionUtils';
import { setVersionInfo } from './slicers/VersionSlice';
import { useLoggerSync } from './services/useLoggerSync';

const App = () => {
    const dispatch = useDispatch();
    const axiosConfig = useSelector(state => state.axiosConfig);
    const userRole = useSelector((state) => state.userInfo.roleCategory);
    const userId = useSelector((state) => state.userInfo.id);
    const baseURL = axiosConfig.envURL.replace(/^http(s)?:/, 'ws$1:').replace(/\/$/, '');
    const checkIntervalRef = useRef(null);

    // Detect browser on app initialization
    useEffect(() => {
        const browserInfo = detectBrowser();
        dispatch(setBrowserInfo(browserInfo));
    }, [dispatch]);

    // Initialize version checking (use backend /latestVersion when envURL is set; works with V1 and V2)
    useEffect(() => {
        const checkVersion = async () => {
            const updateInfo = await checkForUpdates(axiosConfig.envURL || undefined);
            dispatch(setVersionInfo(updateInfo));
        };

        checkVersion();
        checkIntervalRef.current = setInterval(checkVersion, 30 * 1000);

        return () => {
            if (checkIntervalRef.current) {
                clearInterval(checkIntervalRef.current);
            }
        };
    }, [dispatch, axiosConfig.envURL]);

    // Sync auth tokens for logging with Redux state (same as iOS app's approach)
    useLoggerSync();

    return (
        <Router>
            <BrowserWarningBanner />
            <UpdateBanner />
            <PasswordResetModal />
            <CookieBanner />
            <Routes>
                <Route
                    path="*"
                    element={
                        <ProtectedRoute>
                            <WebSocketProvider baseURL={baseURL} userId={userId} userRole={userRole}>
                                <Main />
                            </WebSocketProvider>
                        </ProtectedRoute>
                    }
                />
                <Route path="/" element={<Login />} />
                <Route path="/activate" element={<ActivateAccount />} />
                <Route path="/select-organization" element={<SelectOrganization />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/instructions" element={<InstructionsFromDocx />} />
                <Route path="/support" element={<Support />} />
            </Routes>
        </Router>
    );
};

export default App;
